import { Button, Flex, HStack, Icon, Stack, Tag, Text, useToast } from "@chakra-ui/react";
import { DocumentsAsOwnerTable } from "../../../common/documents/DocumentsAsOwnerTable";
import { isEqual } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiAlertTriangle, FiEdit3, FiPaperclip, FiSend } from "react-icons/fi";
import {
  MarketDialogueAnswerStatus,
  type QuestionAnswerDto,
  type SupplierMarketDialogueDto,
  type ViewDocumentAsOwnerDto,
  useSaveMarketDialogueAnswerMutation,
  useSendMarketDialogueAnswerMutation,
} from "../../../autogen/bff-api";
import { CombinedDocuments } from "../../../common/documents/CombinedDocuments";
import {
  isIncompleteFileUpload,
  type IncompleteFileUpload,
  UploadCompletedStatus,
} from "../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../common/documents/UppyUploader/useUppyUploader";
import { useApiError } from "../../../common/errors/useApiError";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { isTiptapContentEmpty } from "../../../common/input/TipTap/TipTap";
import { useDebouncedSave } from "../../../common/input/useDebouncedSave";
import { SectionCard } from "../../sourcing-events/portal/view-single/SectionCard";
import { isTooLong, lacksSecretReason, needsChoice, MarketDialogueQuestionCard, type QuestionAnswerState } from "./MarketDialogueQuestionCard";

interface Props {
  supplierDialogue: SupplierMarketDialogueDto;
  refetch: () => void;
}

type Answers = Record<string, QuestionAnswerState>;

const EMPTY_ANSWER: QuestionAnswerState = { text: "", choice: null, secretReason: null, secretDocuments: {} };

const answersFrom = (supplierDialogue: SupplierMarketDialogueDto): Answers =>
  Object.fromEntries(
    (supplierDialogue.answer?.answers ?? []).map((answer) => [
      answer.questionId,
      {
        text: answer.text ?? "",
        choice: answer.choice ?? null,
        secretReason: answer.secretReason ?? null,
        secretDocuments: Object.fromEntries((answer.secretDocuments ?? []).map((s) => [s.documentId, s.reason])),
      },
    ]),
  );

const documentsFor = (supplierDialogue: SupplierMarketDialogueDto, questionId: string): ViewDocumentAsOwnerDto[] =>
  supplierDialogue.answer?.answers.find((answer) => answer.questionId === questionId)?.documents ?? [];

export const MarketDialogueAnswerForm = ({ supplierDialogue, refetch }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const toast = useToast();
  const [current, setCurrent] = useState(supplierDialogue);
  useEffect(() => setCurrent(supplierDialogue), [supplierDialogue]);
  const { dialogue, answer, canAnswer } = current;
  const [uploadingQuestions, setUploadingQuestions] = useState<string[]>([]);
  const [typedAnswers, setAnswers] = useState<Answers>(() => answersFrom(supplierDialogue));
  const answers = withMarksOnAttachedDocuments(typedAnswers, current);
  const [saveAnswer] = useSaveMarketDialogueAnswerMutation();
  const [sendAnswer, { isLoading: isSending }] = useSendMarketDialogueAnswerMutation();
  const answersOf = (typed: Answers): QuestionAnswerDto[] =>
    dialogue.questions.flatMap((question) => {
      if (!question.id) return [];
      const typedAnswer = typed[question.id] ?? EMPTY_ANSWER;
      const secretDocuments = Object.entries(typedAnswer.secretDocuments)
        .filter(([, reason]) => reason.trim())
        .map(([documentId, reason]) => ({ documentId, reason }));
      return [
        {
          questionId: question.id,
          text: typedAnswer.text,
          ...(typedAnswer.choice !== null && { choice: typedAnswer.choice }),
          ...(typedAnswer.secretReason?.trim() && { secretReason: typedAnswer.secretReason }),
          ...(secretDocuments.length > 0 && { secretDocuments }),
        },
      ];
    });

  const save = useDebouncedSave(async (typed: Answers) => {
    try {
      setCurrent(
        await saveAnswer({
          marketDialogueId: dialogue.id,
          saveMarketDialogueAnswerRequest: { answers: answersOf(typed) },
        }).unwrap(),
      );
    } catch (e) {
      trigger(e);
      refetch();
    }
  }, 500);

  const change = (questionId: string, changed: QuestionAnswerState) => {
    const next = { ...answers, [questionId]: changed };
    setAnswers(next);
    if (Object.values(next).some(isTooLong)) {
      save.cancel();
      return;
    }
    void save(next);
  };

  const send = async () => {
    save.cancel();
    try {
      setCurrent(
        await sendAnswer({
          marketDialogueId: dialogue.id,
          saveMarketDialogueAnswerRequest: { answers: answersOf(answers) },
        }).unwrap(),
      );
      toast({ title: t("Your answer was sent to the buyer"), status: "success" });
    } catch (e) {
      trigger(e);
      refetch();
    }
  };

  const setUploading = useCallback(
    (questionId: string, uploading: boolean) =>
      setUploadingQuestions((ids) =>
        uploading ? (ids.includes(questionId) ? ids : [...ids, questionId]) : ids.filter((id) => id !== questionId),
      ),
    [],
  );

  const status = answer?.status;
  const typedSinceSaved = !isEqual(written(answers), written(answersFrom(current)));
  const unsent = status !== MarketDialogueAnswerStatus.Sent || typedSinceSaved;
  const documentCount = (questionId: string) => documentsFor(current, questionId).length;
  const nothingToSend =
    Object.values(answers).every((typed) => !hasContent(typed)) &&
    dialogue.questions.every((question) => !question.id || documentCount(question.id) === 0);
  const anyTooLong = Object.values(answers).some(isTooLong);
  const anyChoiceMissing = dialogue.questions.some(
    (question) => !!question.id && needsChoice(question, answers[question.id] ?? EMPTY_ANSWER),
  );
  const holdBack =
    uploadingQuestions.length > 0
      ? t("Wait until the documents are uploaded")
      : anyTooLong
        ? t("Shorten the answers that are too long before you send.")
        : anyChoiceMissing
          ? t("Choose Yes or No on each question you commented on before you send.")
          : Object.values(answers).some(lacksSecretReason)
            ? t("Say why each business secret is one before you send.")
            : nothingToSend
              ? t("Answer at least one question before you send.")
              : null;

  return (
    <SectionCard icon={FiEdit3} title={t("Your answer")}>
      <Stack spacing="6">
        <AnswerStatus supplierDialogue={current} savesHeldBack={anyTooLong} />
        {canAnswer && anyTooLong && (
          <HStack spacing="2" color="smWarning" role="status">
            <Icon as={FiAlertTriangle} aria-hidden />
            <Text fontSize="sm">{t("Changes are not saved until the answers that are too long are shortened.")}</Text>
          </HStack>
        )}
        {!canAnswer && !answer ? (
          <Text color="smMuted">{t("Your company did not answer this market dialogue.")}</Text>
        ) : (
          <>
            {dialogue.questions.map((question, index) =>
              question.id ? (
                <MarketDialogueQuestionCard
                  key={question.id}
                  question={question}
                  number={index + 1}
                  answer={answers[question.id] ?? EMPTY_ANSWER}
                  documents={documentsFor(current, question.id)}
                  canAnswer={canAnswer}
                  onChange={(changed) => change(question.id ?? "", changed)}
                  attachments={
                    answer ? (
                      <AnswerDocuments
                        answerId={answer.id}
                        marketDialogueId={dialogue.id}
                        questionId={question.id}
                        documents={documentsFor(current, question.id)}
                        canAnswer={canAnswer}
                        refetch={refetch}
                        onUploadingChange={setUploading}
                      />
                    ) : (
                      canAnswer && (
                        <Button
                          size="sm"
                          variant="outline"
                          leftIcon={<FiPaperclip />}
                          onClick={() => {
                            void save(answers);
                            void save.flush();
                          }}
                        >
                          {t("Attach documents")}
                        </Button>
                      )
                    )
                  }
                />
              ) : null,
            )}
            {(answer?.otherDocuments?.length ?? 0) > 0 && (
              <Stack spacing="2">
                <Text fontWeight="medium">{t("Other documents")}</Text>
                <Text fontSize="sm" color="smMuted">
                  {t("Attached to the whole answer before each document belonged to a question.")}
                </Text>
                <DocumentsAsOwnerTable documents={answer?.otherDocuments ?? []} />
              </Stack>
            )}
            {canAnswer && (
              <Flex direction="column" align="flex-end" gap="2">
                <Button
                  colorScheme="teal"
                  leftIcon={<FiSend />}
                  onClick={send}
                  isLoading={isSending}
                  isDisabled={!unsent || holdBack !== null}
                >
                  {status === MarketDialogueAnswerStatus.Draft || !status ? t("Send answer") : t("Send changes")}
                </Button>
                {unsent && holdBack && (
                  <Text fontSize="sm" color="smMuted">
                    {holdBack}
                  </Text>
                )}
              </Flex>
            )}
          </>
        )}
      </Stack>
    </SectionCard>
  );
};

const hasContent = (answer: QuestionAnswerState) => !isTiptapContentEmpty(answer.text) || answer.choice !== null;

const withMarksOnAttachedDocuments = (answers: Answers, supplierDialogue: SupplierMarketDialogueDto): Answers =>
  Object.fromEntries(
    Object.entries(answers).map(([questionId, answer]) => {
      const attached = new Set(documentsFor(supplierDialogue, questionId).map((document) => document.id));
      const secretDocuments = Object.fromEntries(
        Object.entries(answer.secretDocuments).filter(([documentId]) => attached.has(documentId)),
      );
      return [questionId, { ...answer, secretDocuments }];
    }),
  );

const written = (answers: Answers) =>
  Object.fromEntries(
    Object.entries(answers).filter(
      ([, answer]) =>
        hasContent(answer) || answer.secretReason !== null || Object.keys(answer.secretDocuments).length > 0,
    ),
  );

const AnswerStatus = ({
  supplierDialogue,
  savesHeldBack,
}: {
  supplierDialogue: SupplierMarketDialogueDto;
  savesHeldBack: boolean;
}) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const { answer, canAnswer } = supplierDialogue;
  const sentAt = answer?.sentAt;
  switch (answer?.status) {
    case MarketDialogueAnswerStatus.Sent:
      return (
        <Flex gap="2" align="center" wrap="wrap">
          <Tag colorScheme="green" variant="outline">
            {t("Sent")}
          </Tag>
          {sentAt && (
            <Text fontSize="sm" color="smMuted">
              {t("Sent to the buyer {{date}}", { date: displayDateWithTime(sentAt) })}
            </Text>
          )}
        </Flex>
      );
    case MarketDialogueAnswerStatus.ChangedSinceSent:
      return (
        <Flex gap="2" align="center" wrap="wrap">
          <Tag colorScheme="orange" variant="outline">
            {t("Changes not sent")}
          </Tag>
          <Text fontSize="sm" color="smMuted">
            {t("The buyer sees the answer you sent {{date}} until you send your changes.", {
              date: sentAt ? displayDateWithTime(sentAt) : "",
            })}
          </Text>
        </Flex>
      );
    default:
      if (!canAnswer && answer) {
        return (
          <Flex gap="2" align="center" wrap="wrap">
            <Tag colorScheme="gray" variant="outline">
              {t("Not sent")}
            </Tag>
            <Text fontSize="sm" color="smMuted">
              {t("The deadline passed before this answer was sent. The buyer has not received it.")}
            </Text>
          </Flex>
        );
      }
      return canAnswer && !savesHeldBack ? (
        <Text fontSize="sm" color="smMuted">
          {t("Your answer is saved as a draft while you write. The buyer sees it only once you send it.")}
        </Text>
      ) : null;
  }
};

const AnswerDocuments = ({
  answerId,
  marketDialogueId,
  questionId,
  documents,
  canAnswer,
  refetch,
  onUploadingChange,
}: {
  answerId: string;
  marketDialogueId: string;
  questionId: string;
  documents: ViewDocumentAsOwnerDto[];
  canAnswer: boolean;
  refetch: () => void;
  onUploadingChange: (questionId: string, uploading: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { uppy, allFiles, documents: shown, setDocuments, removeUpload } = useUppyUploader({
    entityId: answerId,
    eventId: marketDialogueId,
    entityType: "MarketDialogueAnswer",
    reloadEntity: refetch,
    questionId,
  });
  const uploading = allFiles.some((file) => isIncompleteFileUpload(file));

  useEffect(() => {
    setDocuments(documents);
  }, [documents, setDocuments]);

  useEffect(() => onUploadingChange(questionId, uploading), [questionId, uploading, onUploadingChange]);

  const onRemove = (document: ViewDocumentAsOwnerDto) =>
    removeUpload({
      status: UploadCompletedStatus.UploadCompleted,
      documentId: document.id,
      uploadId: document.fromUploadId,
      ...document,
    });

  return (
    <CombinedDocuments
      uploadedDocuments={shown}
      setUploadedDocuments={setDocuments}
      removeUploadedDocument={canAnswer ? onRemove : undefined}
      canDeleteDocuments={canAnswer}
      documentUploadConfig={
        canAnswer
          ? {
              uppy,
              dropTargetId: `marketDialogueAnswerFileTarget-${questionId}`,
              dropTargetHelperText: t("Drag and drop files here, or click to browse"),
              uploads: allFiles.filter((f) => isIncompleteFileUpload(f)) as IncompleteFileUpload[],
              removeUpload,
            }
          : undefined
      }
      isTagsColumnVisible={false}
    />
  );
};
